// src/features/users/components/UserManagement.jsx
import React, { useState, useEffect } from 'react';
import { useUsers } from '../../../hooks/useUsers';
import { useAuth } from '../../../hooks/useAuth';
import UsersList from './UsersList';
import UserForm from './UserForm';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import ErrorMessage from '../../../components/UI/ErrorMessage';
import UnauthorizedMessage from '../../../components/UI/UnauthorizedMessage';
import { 
    PlusIcon, 
    UsersIcon, 
    MagnifyingGlassIcon, 
    FunnelIcon,
    ArrowPathIcon 
} from '@heroicons/react/24/outline';

const UserManagement = () => {
    const { user, isSupervisor, isManager } = useAuth();
    const { 
        users, 
        loading, 
        error, 
        createUser, 
        updateUser, 
        deleteUser,
        updateUserRole,
        updateUserValorHora,
        fetchUsers 
    } = useUsers();

    const [showUserForm, setShowUserForm] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    // Verificar permisos
    const canManageUsers = isSupervisor || isManager;

    // Si no tiene permisos, mostrar mensaje de no autorizado
    if (!canManageUsers) {
        return (
            <UnauthorizedMessage 
                title="Gestión de Usuarios - Acceso restringido"
                message="Solo los supervisores y gerentes pueden acceder a la gestión de usuarios."
            />
        );
    }

    const handleCreateUser = async (userData) => {
        try {
            await createUser(userData);
            setShowUserForm(false);
        } catch (error) {
            throw error;
        }
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setShowUserForm(true);
    };

    const handleUpdateUser = async (userData) => {
        try {
            await updateUser(editingUser.id, userData);
            setEditingUser(null);
            setShowUserForm(false);
        } catch (error) {
            alert(`Error: ${error.message || 'No se pudo actualizar el usuario'}`);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await updateUserRole(userId, newRole);
        } catch (error) {
            console.error('Error actualizando rol:', error);
        }
    };

    const handleValorHoraEdit = (user) => {
        const newValorHora = prompt(`Ingrese el nuevo valor por hora para ${user.username}:`, user.valor_hora || '0');
        if (newValorHora !== null) {
            updateUserValorHora(user.id, parseFloat(newValorHora) || 0);
        }
    };

    const handleCancelForm = () => {
        setShowUserForm(false);
        setEditingUser(null);
    };

    const handleRefresh = () => {
        fetchUsers();
    };

    // Filtrar usuarios basado en búsqueda y filtros
    const filteredUsers = users.filter(user => {
        const matchesSearch = 
            user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.full_name?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesRole = roleFilter === 'all' || user.role === roleFilter;
        const matchesStatus = 
            statusFilter === 'all' || 
            (statusFilter === 'active' && user.is_active) ||
            (statusFilter === 'inactive' && !user.is_active);

        return matchesSearch && matchesRole && matchesStatus;
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <LoadingSpinner text="Cargando usuarios..." />
            </div>
        );
    }

    if (error && !loading) {
        return (
            <ErrorMessage 
                error={error} 
                onRetry={fetchUsers}
                title="Error al cargar usuarios"
                message="No se pudieron cargar los usuarios del sistema. Puede ser un problema de permisos o de conexión."
            />
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
            {/* Header con botón de acción */}
            <div className="mb-6">
                <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center">
                    <UsersIcon className="h-8 w-8 text-blue-600 mr-3" />
                    <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
                    <p className="text-sm sm:text-base text-gray-600">
                        {filteredUsers.length} usuario{filteredUsers.length !== 1 ? 's' : ''} de {users.length} total
                        {user && (
                        <span className="ml-2 text-xs text-gray-500">
                            (Sesión: {user.username} - {user.role})
                        </span>
                        )}
                    </p>
                    </div>
                </div>
                
                <div className="flex items-center space-x-3">
                    <button
                    onClick={handleRefresh}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    title="Actualizar lista"
                    >
                    <ArrowPathIcon className="h-4 w-4" />
                    </button>
                    
                    <button
                    onClick={() => setShowUserForm(true)}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
                    >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Nuevo Usuario
                    </button>
                </div>
                </div>

                {/* Filtros y Búsqueda */}
                <div className="mt-4 space-y-3 sm:space-y-0 sm:flex sm:space-x-4">
                    {/* Barra de búsqueda */}
                    <div className="flex-1 max-w-md">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Buscar usuarios..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>
                    </div>

                    {/* Filtro por rol */}
                    <div className="sm:w-40">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FunnelIcon className="h-4 w-4 text-gray-400" />
                            </div>
                            <select
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            >
                                <option value="all">Todos los roles</option>
                                <option value="professional">Profesional</option>
                                <option value="supervisor">Supervisor</option>
                                <option value="manager">Gerente</option>
                            </select>
                        </div>
                    </div>

                    {/* Filtro por estado */}
                    <div className="sm:w-40">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                            <option value="all">Todos los estados</option>
                            <option value="active">Activos</option>
                            <option value="inactive">Inactivos</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Modal para crear/editar usuario */}
            {showUserForm && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-4 sm:top-20 mx-auto p-3 sm:p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
                        <div className="mt-3">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">
                                {editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
                            </h3>
                            <button
                                onClick={handleCancelForm}
                                className="text-gray-400 hover:text-gray-600 text-2xl"
                            >
                                ×
                            </button>
                        </div>
                        
                        <UserForm
                            user={editingUser}
                            onSubmit={editingUser ? handleUpdateUser : handleCreateUser}
                            onCancel={handleCancelForm}
                            currentUserRole={user?.role}
                        />
                        </div>
                    </div>
                </div>
            )}

            {/* Lista de usuarios */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                {filteredUsers.length > 0 ? (
                    <UsersList 
                        users={filteredUsers}
                        onEdit={handleEditUser}
                        onRoleChange={handleRoleChange}
                        onValorHoraEdit={handleValorHoraEdit}
                        onDelete={deleteUser}
                        currentUser={user}
                    />
                ) : (
                <div className="text-center py-12">
                    <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No hay usuarios</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        No se encontraron usuarios que coincidan con los criterios.
                    </p>
                </div>
                )}
            </div>

            {/* Información de permisos */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                    <svg className="h-5 w-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                        <p className="text-sm font-medium text-blue-800">Permisos de gestión de usuarios</p>
                        <p className="text-xs text-blue-600 mt-1">
                            Los supervisores y gerentes pueden gestionar usuarios. 
                            Los gerentes pueden asignar roles de supervisor y manager.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserManagement;
